import type {
  AnnualRevenue,
  ProductDescription,
} from "../../onboarding/OnboardingShopInfoContent";

import * as React from "react";

import { useCurrentWorkspace } from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { onboardingRouter, onboardingUtils } from "@editor/trpc/onboarding";
import { projectRouter, projectUtils } from "@editor/trpc/project";
import { userUtils } from "@editor/trpc/user";
import { workspaceRouter, workspaceUtils } from "@editor/trpc/workspace";

import Button from "@replo/design-system/components/button/Button";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { skipToken } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { isNullish } from "replo-utils/lib/misc";

import OnboardingShopInfoContent from "../../onboarding/OnboardingShopInfoContent";

interface ShopInfoProps {
  selectedWorkspaceId?: string;
  onContinue?: (
    projectId?: string,
    shopDetails?: {
      shopName: string;
      subdomain: string;
      productDescription: ProductDescription | null;
      annualRevenue: AnnualRevenue | null;
    },
  ) => void;
  onClose?: () => void;
}

export default function ShopInfo({
  selectedWorkspaceId,
  onContinue,
  onClose,
}: ShopInfoProps) {
  const [shopName, setShopName] = React.useState<string>("");
  const [subdomain, setSubdomain] = React.useState<string>("");
  const [productDescription, setProductDescription] =
    React.useState<ProductDescription | null>(null);
  const [annualRevenue, setAnnualRevenue] =
    React.useState<AnnualRevenue | null>(null);
  const [isFormValid, setIsFormValid] = React.useState(false);

  const { user } = useCurrentUser();
  const currentWorkspace = useCurrentWorkspace();

  const { data: selectedWorkspaceData, isLoading: isLoadingSelectedWorkspace } =
    workspaceRouter.getById.useQuery(
      selectedWorkspaceId ? { id: selectedWorkspaceId } : skipToken,
    );
  const selectedWorkspace = selectedWorkspaceData?.workspace;
  const workspace = selectedWorkspace ?? currentWorkspace;
  const workspaceId = selectedWorkspaceId ?? workspace?.id;

  const isShopifyPaymentProcessor =
    workspace?.preferredPaymentProcessor === "shopify";

  const { mutateAsync: createShop, isPending: isCreatingShop } =
    projectRouter.create.useMutation();

  const {
    mutateAsync: updateAnnualRevenueAsync,
    isPending: isUpdatingAnnualRevenue,
  } = onboardingRouter.updateAnnualRevenue.useMutation();

  const isNextDisabled =
    !isFormValid ||
    isNullish(annualRevenue) ||
    (isShopifyPaymentProcessor ? false : productDescription === null) ||
    shopName === "" ||
    (!isShopifyPaymentProcessor && subdomain === "") ||
    !workspaceId ||
    (selectedWorkspaceId && isLoadingSelectedWorkspace);

  const mapRevenueToNumber = (revenue: string | null): number => {
    switch (revenue) {
      case "0-500k":
        return 0;
      case "500k-2m":
        return 500000;
      case "2m-5m":
        return 2000000;
      case "5m-10m":
        return 5000000;
      case "10m-50m":
        return 10000000;
      case "50m+":
        return 50000000;
      default:
        return 0;
    }
  };

  const handleContinue = async () => {
    const isCompleteFormValid =
      !isNullish(annualRevenue) &&
      (isShopifyPaymentProcessor || productDescription !== null) &&
      shopName !== "" &&
      (isShopifyPaymentProcessor || subdomain !== "") &&
      isFormValid &&
      user &&
      workspaceId;

    if (!isCompleteFormValid) {
      return;
    }

    if (productDescription === "shopify" && !isShopifyPaymentProcessor) {
      onContinue?.(undefined, {
        shopName,
        subdomain,
        productDescription,
        annualRevenue,
      });
      return;
    }

    await updateAnnualRevenueAsync({
      annualRevenue: mapRevenueToNumber(annualRevenue),
    });

    const result = await createShop({
      name: shopName.trim(),
      workspaceId: workspaceId,
      hostingType: isShopifyPaymentProcessor ? "shopify" : "replo",
      shopDomainPrefix: isShopifyPaymentProcessor ? null : subdomain.trim(),
      productDescription: isShopifyPaymentProcessor
        ? "shopify"
        : productDescription,
    });

    await workspaceUtils.getUserWorkspacesList.invalidate();
    await projectUtils.listWithStats.invalidate();
    await userUtils.get.invalidate();
    await projectUtils.listGroupedByWorkspaceForUser.invalidate();
    await onboardingUtils.checkSubdomainAvailability.invalidate();

    onContinue?.(result.project?.id, {
      shopName,
      subdomain,
      productDescription,
      annualRevenue,
    });
  };

  return (
    <Modal
      isOpen
      onOpenChange={(open) => {
        if (!open) {
          onClose?.();
        }
      }}
      size="custom"
      title="Tell us about your shop"
      description="We'll use this info to tailor recommendations for you."
      layoutClassName="h-[900px] w-[1100px]"
      UNSAFE_className="[&_.header-h2]:text-center [&_.header-h2]:text-3xl [&_.header-h2]:pt-4 [&_p.text-muted]:text-center"
      footer={
        <div className="w-full">
          <div className="flex justify-end items-center w-full pt-4 border-t-[0.5px] border-border -mx-6">
            <Button
              variant="primary"
              size="base"
              onClick={() => void handleContinue()}
              disabled={
                isNextDisabled || isCreatingShop || isUpdatingAnnualRevenue
              }
              isLoading={
                isCreatingShop ||
                isUpdatingAnnualRevenue ||
                Boolean(selectedWorkspaceId && isLoadingSelectedWorkspace)
              }
              endEnhancer={<ArrowRight size={16} />}
            >
              Continue
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex items-start justify-center min-h-0 flex-1 overflow-y-auto py-6 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="lg:w-[1000px] w-full px-5 lg:px-0">
          <OnboardingShopInfoContent
            shopName={shopName}
            setShopName={setShopName}
            subdomain={subdomain}
            setSubdomain={setSubdomain}
            productDescription={productDescription}
            setProductDescription={setProductDescription}
            annualRevenue={annualRevenue}
            setAnnualRevenue={setAnnualRevenue}
            isBusinessContext={true}
            isShopifyPaymentProcessor={isShopifyPaymentProcessor}
            onFormValidityChange={setIsFormValid}
          />
        </div>
      </div>
    </Modal>
  );
}
